/**
 * Каталог прав. Право — пара «модуль.действие». Роль — набор прав,
 * назначение роли — на всю компанию, на юрлицо или на проект (раздел 4 ТЗ).
 */

export interface PermissionDef {
  code: string;
  title: string;
}

export interface PermissionGroup {
  module: string;
  title: string;
  permissions: PermissionDef[];
}

export const PERMISSION_GROUPS = [
  {
    module: 'portfolio',
    title: 'Портфель и аналитика',
    permissions: [
      { code: 'portfolio.view', title: 'Видеть портфель проектов' },
      { code: 'analytics.view', title: 'Видеть аналитику и дашборды' },
      { code: 'analytics.report.edit', title: 'Создавать и править отчёты конструктора' },
      { code: 'analytics.report.export', title: 'Выгружать отчёты в Excel и PDF' },
      { code: 'lists.view', title: 'Видеть настраиваемые списки разделов' },
    ],
  },
  {
    module: 'project',
    title: 'Проекты',
    permissions: [
      { code: 'project.view', title: 'Видеть карточку проекта' },
      { code: 'project.create', title: 'Создавать проекты' },
      { code: 'project.edit', title: 'Менять проект и объекты' },
      { code: 'project.archive', title: 'Архивировать проекты' },
    ],
  },
  {
    module: 'psd',
    title: 'Проектная документация',
    permissions: [
      { code: 'psd.view', title: 'Видеть ПСД' },
      { code: 'psd.upload', title: 'Загружать комплекты и ревизии' },
      { code: 'psd.approve', title: 'Утверждать ревизии' },
      { code: 'psd.stamp', title: 'Ставить штамп «В производство работ»' },
    ],
  },
  {
    module: 'estimates',
    title: 'Сметы и нормативная база',
    permissions: [
      { code: 'estimate.view', title: 'Видеть сметы' },
      { code: 'estimate.edit', title: 'Составлять и править сметы' },
      { code: 'estimate.approve', title: 'Утверждать сметы' },
      { code: 'norms.view', title: 'Видеть нормативную базу' },
      { code: 'norms.manage', title: 'Загружать выпуски нормативной базы' },
    ],
  },
  {
    module: 'budget',
    title: 'Бюджет и маржа',
    permissions: [
      { code: 'budget.view', title: 'Видеть бюджет и маржу' },
      { code: 'budget.edit', title: 'Править бюджет и варианты исполнения' },
      { code: 'budget.approve', title: 'Утверждать бюджет' },
    ],
  },
  {
    module: 'schedule',
    title: 'График',
    permissions: [
      { code: 'schedule.view', title: 'Видеть график' },
      { code: 'schedule.edit', title: 'Править график' },
      { code: 'schedule.baseline', title: 'Сохранять и назначать базовые планы' },
    ],
  },
  {
    module: 'resources',
    title: 'Ресурсы',
    permissions: [
      { code: 'resources.view', title: 'Видеть ресурсы и загрузку' },
      { code: 'resources.edit', title: 'Вести справочник ресурсов, вахты и персонал' },
      { code: 'timesheet.approve', title: 'Согласовывать табели' },
    ],
  },
  {
    module: 'procurement',
    title: 'Закупки',
    permissions: [
      { code: 'procurement.view', title: 'Видеть закупки' },
      { code: 'procurement.request.create', title: 'Создавать заявки на закуп' },
      { code: 'procurement.request.review', title: 'Проверять заявки' },
      { code: 'procurement.rfq.manage', title: 'Запрашивать и вносить КП' },
      { code: 'procurement.analysis', title: 'Проводить коммерческий анализ и выбирать поставщика' },
      { code: 'procurement.approve.limit', title: 'Согласовывать выбор сверх лимита' },
      { code: 'procurement.registry', title: 'Вести реестры закупок' },
      { code: 'procurement.payment.request', title: 'Готовить заявки на оплату и данные 1С' },
      { code: 'procurement.payment.confirm', title: 'Фиксировать факт оплаты' },
      { code: 'procurement.dashboard', title: 'Видеть экран руководителя закупок' },
      { code: 'suppliers.view', title: 'Видеть справочник поставщиков' },
      { code: 'suppliers.edit', title: 'Вести справочник поставщиков' },
    ],
  },
  {
    module: 'contracts',
    title: 'Договоры',
    permissions: [
      { code: 'contracts.view', title: 'Видеть договоры' },
      { code: 'contracts.edit', title: 'Готовить договоры и допсоглашения' },
      { code: 'contracts.approve', title: 'Согласовывать договоры: любой пункт маршрута' },
      { code: 'contracts.approve.accounting', title: 'Согласовывать договоры за бухгалтерию' },
      { code: 'contracts.approve.legal', title: 'Согласовывать договоры за юридическую службу' },
      { code: 'contracts.approve.deputy', title: 'Согласовывать договоры как заместитель директора' },
      { code: 'contracts.approve.security', title: 'Согласовывать договоры за службу безопасности' },
      { code: 'contracts.sign', title: 'Подписывать договоры' },
      { code: 'contracts.docs', title: 'Вести оригиналы, сканы и реестр договоров' },
    ],
  },
  {
    module: 'site',
    title: 'Площадка',
    permissions: [
      { code: 'site.view', title: 'Видеть отчёты площадки' },
      { code: 'site.report', title: 'Сдавать ежедневные отчёты' },
      { code: 'site.review', title: 'Проверять и принимать отчёты' },
    ],
  },
  {
    module: 'supervision',
    title: 'Надзор и вопросы к проекту',
    permissions: [
      { code: 'supervision.view', title: 'Видеть реестр замечаний и вопросов' },
      { code: 'supervision.issue', title: 'Выдавать предписания и замечания' },
      { code: 'supervision.respond', title: 'Отвечать и устранять' },
    ],
  },
  {
    module: 'changes',
    title: 'Изменения',
    permissions: [
      { code: 'changes.view', title: 'Видеть запросы на изменение' },
      { code: 'changes.request', title: 'Создавать запросы на изменение' },
      { code: 'changes.approve', title: 'Согласовывать изменения' },
    ],
  },
  {
    module: 'operations',
    title: 'Склад, финансы, качество, документы',
    permissions: [
      { code: 'warehouse.view', title: 'Видеть склад' },
      { code: 'warehouse.operate', title: 'Проводить складские операции' },
      {
        code: 'warehouse.approve',
        title: 'Решать по складу: выдача сверх лимита, перерасход, потеря, недостача, ведомость расхождений',
      },
      { code: 'warehouse.export', title: 'Обмен склада с 1С' },
      { code: 'finance.export', title: 'Выгрузка заявок на оплату в 1С и настройка сопоставления полей' },
      { code: 'finance.view', title: 'Видеть финансы проекта' },
      { code: 'finance.edit', title: 'Вести платёжный календарь' },
      {
        code: 'finance.manager',
        title:
          'Финансовый менеджер: комплекты документов к оплате, лимиты платежей, решения по повторной оплате',
      },
      { code: 'qhse.view', title: 'Видеть качество и ОТ' },
      { code: 'qhse.edit', title: 'Вести проверки, наряды-допуски, инструктажи' },
      {
        code: 'qhse.personal',
        title: 'Видеть персональные данные работников: удостоверения и их сканы (ДК-11)',
      },
      { code: 'documents.view', title: 'Видеть документы' },
      { code: 'documents.edit', title: 'Загружать и править документы' },
    ],
  },
  {
    module: 'video',
    title: 'Видеонаблюдение',
    permissions: [
      { code: 'video.view', title: 'Смотреть камеры, кадры и события' },
      { code: 'video.decide', title: 'Решать по событиям видеоаналитики' },
      { code: 'video.manage', title: 'Заводить и менять камеры проекта' },
      {
        code: 'video.biometrics',
        title: 'Согласия на биометрию, распознавание лиц и ключи видеосервера',
      },
      { code: 'video.audit', title: 'Видеть журнал просмотров видео' },
    ],
  },
  {
    module: 'kb',
    title: 'База знаний',
    permissions: [
      { code: 'kb.view', title: 'Читать базу знаний' },
      { code: 'kb.edit', title: 'Писать и править статьи' },
      { code: 'kb.publish', title: 'Публиковать любые статьи и видеть черновики' },
    ],
  },
  {
    module: 'support',
    title: 'Поддержка',
    permissions: [
      { code: 'support.handle', title: 'Отвечать на обращения и разбирать очередь поддержки' },
      { code: 'support.triage', title: 'Менять состояние предложений по развитию портала' },
    ],
  },
  {
    module: 'ai',
    title: 'ИИ-помощник',
    permissions: [{ code: 'ai.use', title: 'Пользоваться ИИ-помощником' }],
  },
  {
    module: 'admin',
    title: 'Администрирование',
    permissions: [
      { code: 'admin.users', title: 'Управлять пользователями и приглашениями' },
      { code: 'admin.roles', title: 'Настраивать роли и права' },
      { code: 'admin.org', title: 'Вести юрлица группы и справочники' },
      { code: 'admin.audit', title: 'Видеть историю изменений по всей системе' },
      {
        code: 'admin.revert',
        title: 'Отменять изменения из журнала, восстанавливать удалённое (с проверкой и причиной)',
      },
      { code: 'admin.settings', title: 'Менять настройки портала' },
      { code: 'roadmap.view', title: 'Видеть ход разработки' },
    ],
  },
] as const satisfies readonly PermissionGroup[];

export type PermissionCode = (typeof PERMISSION_GROUPS)[number]['permissions'][number]['code'];

export const ALL_PERMISSIONS: PermissionCode[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.code),
);

const PERMISSION_SET = new Set<string>(ALL_PERMISSIONS);

export function isPermissionCode(code: string): code is PermissionCode {
  return PERMISSION_SET.has(code);
}

export type ScopeType = 'tenant' | 'legal_entity' | 'direction' | 'project';

/** Право, действующее в пределах области: компания целиком, юрлицо или проект. */
export interface ScopedPermission {
  permission: PermissionCode;
  scopeType: ScopeType;
  scopeId: string | null;
}
